import { useEffect, useState } from 'react';

interface Props {
  onDone: () => void;
}

export default function SplashScreen({ onDone }: Props) {
  const [phase, setPhase] = useState<'enter' | 'hold' | 'exit'>('enter');

  useEffect(() => {
    // Phase 1 — éléments entrent (0.8s)
    const t1 = setTimeout(() => setPhase('hold'), 800);
    // Phase 2 — hold (1.2s)
    const t2 = setTimeout(() => setPhase('exit'), 2000);
    // Phase 3 — exit fade (0.6s) → done
    const t3 = setTimeout(() => onDone(), 2600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);

  return (
    <div
      className="fixed inset-0 z-[999] flex flex-col items-center justify-center"
      style={{
        background: 'linear-gradient(160deg, #0d1b3e 0%, #1a3a8f 45%, #2563eb 100%)',
        opacity: phase === 'exit' ? 0 : 1,
        transition: phase === 'exit' ? 'opacity 0.6s ease-in-out' : 'none',
        pointerEvents: phase === 'exit' ? 'none' : 'all',
      }}
    >
      {/* Cercles décoratifs de fond */}
      <div
        className="absolute top-[-80px] right-[-60px] w-64 h-64 rounded-full"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      />
      <div
        className="absolute bottom-[-40px] left-[-80px] w-80 h-80 rounded-full"
        style={{ background: 'rgba(255,255,255,0.04)' }}
      />
      <div
        className="absolute top-[30%] left-[-30px] w-32 h-32 rounded-full"
        style={{ background: 'rgba(255,255,255,0.05)' }}
      />

      {/* Logo + Icône */}
      <div
        className="flex flex-col items-center"
        style={{
          opacity: phase === 'enter' ? 0 : 1,
          transform: phase === 'enter' ? 'translateY(28px) scale(0.85)' : 'translateY(0) scale(1)',
          transition: 'opacity 0.6s ease-out, transform 0.6s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        {/* Logo AutoLink */}
        <div
          className="w-28 h-28 rounded-3xl flex items-center justify-center mb-5 shadow-2xl overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)', border: '2px solid rgba(255,255,255,0.25)', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
        >
          <img
            src="https://storage.readdy-site.link/project_files/90f45ec7-b0ce-4237-a8d9-2b6895d1e519/1ed3c010-ddb6-4196-9984-6ab18a486563_ChatGPT-Image-26-juin-2025-15_05_07.png?v=23bbf812ca6f957bf20c41ebf2939508"
            alt="AutoLink Logo"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Nom de l'app */}
        <h1
          className="text-white font-extrabold text-3xl tracking-tight mb-1"
          style={{ fontFamily: '"Montserrat", sans-serif', letterSpacing: '-0.5px' }}
        >
          AutoLink
        </h1>

        {/* Sous-titre */}
        <p
          className="text-sm font-medium tracking-wide"
          style={{
            color: '#93c5fd',
            opacity: phase === 'enter' ? 0 : 1,
            transition: 'opacity 0.5s ease-out 0.2s',
            letterSpacing: '0.04em',
          }}
        >
          Conduisez mieux, gérez plus
        </p>
      </div>

      {/* Badge INDIGO */}
      <div
        className="mt-8 flex items-center gap-2 px-4 py-2 rounded-full"
        style={{
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.2)',
          opacity: phase === 'enter' ? 0 : 1,
          transform: phase === 'enter' ? 'translateY(12px)' : 'translateY(0)',
          transition: 'opacity 0.5s ease-out 0.35s, transform 0.5s ease-out 0.35s',
        }}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-parking-box-fill text-white text-base"></i>
        </div>
        <span className="text-white text-xs font-bold tracking-widest">INDIGO PRO</span>
        <span className="text-blue-200 text-xs">Luxembourg</span>
      </div>

      {/* Barre de chargement animée */}
      <div
        className="absolute bottom-20 flex flex-col items-center gap-3"
        style={{
          opacity: phase === 'enter' ? 0 : 1,
          transition: 'opacity 0.4s ease-out 0.5s',
        }}
      >
        <div className="w-48 h-1 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-white rounded-full"
            style={{
              width: phase === 'hold' || phase === 'exit' ? '100%' : '0%',
              transition: phase === 'hold' ? 'width 1.1s ease-in-out' : 'none',
            }}
          />
        </div>
        <p className="text-blue-200 text-[11px] font-medium">Chargement en cours…</p>
      </div>

      {/* Version */}
      <p
        className="absolute bottom-8 text-blue-300 text-[10px]"
        style={{
          opacity: phase === 'enter' ? 0 : 0.6,
          transition: 'opacity 0.4s ease-out 0.6s',
        }}
      >
        AutoLink v1.0.0 · 2026
      </p>
    </div>
  );
}
